import React, { useState, useEffect } from 'react';
import { Event } from '../types/Event';
import { EventForm, MissingFields } from './EventForm.tsx';
import { GeoPoint, Timestamp } from 'firebase/firestore';
import { storage } from '../firebase/config.ts';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config.ts';
import { eventService } from '../services/eventService.ts';
import { imageService } from '../services/imageService.ts';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { classifyEventType } from '../services/aiService.ts';
import { eventParsers, FacebookJsonEvent, TelAvivJsonEvent, CinemaJsonEvent, MevalimJsonEvent, FunzingJsonEvent } from '../services/eventParsers.ts';
import { EventCategory } from '../constants/eventTypes.ts';

interface LocalImage {
  url: string;
  file: File;
}

interface ClassificationResult {
  category: EventCategory;
  type: string;
}

const fetchImages = async (urls: string[], eventTitle: string): Promise<LocalImage[]> => {
  if (!urls || urls.length === 0) return [];
  const localImages: LocalImage[] = [];
  
  for (let i = 0; i < urls.length; i++) {
    try {
      if (urls[i].includes('tel-aviv.gov.il')) {
        const proxyUrl = process.env.NODE_ENV === 'development' 
          ? `http://localhost:8080/proxy?url=${encodeURIComponent(urls[i])}` 
          : urls[i];

        const response = await fetch(proxyUrl);
        if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
        
        const blob = await response.blob();
        const file = new File([blob], `image_${Date.now()}_${i}.jpg`, { type: 'image/jpeg' });
        const localUrl = URL.createObjectURL(blob);
        localImages.push({ url: localUrl, file });
      } else {
        const response = await fetch(urls[i]);
        if (!response.ok) throw new Error(`Direct fetch failed: ${response.status}`);
        
        const blob = await response.blob();
        const file = new File([blob], `image_${Date.now()}_${i}.jpg`, { type: blob.type });
        const localUrl = URL.createObjectURL(blob);
        localImages.push({ url: localUrl, file });
      }
    } catch (error) {
      console.error(`Error fetching image:`, error.message);
    }
  }
  return localImages;
};

export const JsonImport: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [trailerStatus, setTrailerStatus] = useState<string>('');
  const [missingFields, setMissingFields] = useState<MissingFields>({});
  const [autoUploadCount, setAutoUploadCount] = useState(0);
  const [automaticMode, setAutomaticMode] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>('');
  const [eventsWithMissingFields, setEventsWithMissingFields] = useState<Event[]>([]);
  const [aiClassifying, setAiClassifying] = useState(false);
  const [eventNumberInput, setEventNumberInput] = useState('');
  const [failedEvents, setFailedEvents] = useState<number>(0);
  const [classificationRetries, setClassificationRetries] = useState<number>(0);
  const [processingQueue, setProcessingQueue] = useState<boolean>(false);
  const [lastError, setLastError] = useState<string>('');
  const MAX_RETRIES = 3;
  const BASE_DELAY = 2000; // 2 seconds base delay
  const QUEUE_DELAY = 3000; // 3 seconds between classifications

  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const logError = (error: any, context: string) => {
    const errorMessage = error?.message || String(error);
    const errorDetails = {
      context,
      message: errorMessage,
      timestamp: new Date().toISOString(),
      stack: error?.stack
    };
    console.error('Detailed error log:', errorDetails);
    setLastError(`${context}: ${errorMessage}`);
    return errorDetails;
  };

  const classifyEventWithRetry = async (event: Event, retryCount = 0): Promise<ClassificationResult> => {
    try {
      if (retryCount > 0) {
        const delay = BASE_DELAY * Math.pow(2, retryCount - 1); // Exponential backoff
        console.log(`Waiting ${delay}ms before retry ${retryCount} for event: "${event.title}"`);
        await sleep(delay);
      }

      if (processingQueue) {
        console.log(`Waiting for queue to clear for event: "${event.title}"`);
        await sleep(QUEUE_DELAY);
      }

      setProcessingQueue(true);
      console.log(`Starting classification for event: "${event.title}" (Attempt ${retryCount + 1}/${MAX_RETRIES})`);

      // Use a shorter description to reduce load on the AI service
      const shortDescription = event.description
        .split('\n')
        .slice(0, 3) // Only take first 3 lines
        .join('\n')
        .slice(0, 500) // Limit to 500 characters
        .trim();
      
      console.log('Classification request details:', {
        title: event.title,
        descriptionLength: shortDescription.length,
        attempt: retryCount + 1,
        timestamp: new Date().toISOString()
      });
      
      const response = await fetch('https://us-central1-spontane-gog47y.cloudfunctions.net/classifyEventTypeDetailed', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          title: event.title,
          description: shortDescription
        })
      });

      const responseDetails = {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      };
      console.log('Classification response details:', responseDetails);

      // Handle 500 errors specifically
      if (response.status === 500) {
        console.warn('AI service returned 500 error, will retry');
        throw new Error('AI service temporarily unavailable');
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Classification error response:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
          event: event.title
        });
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      const classification = await response.json();
      console.log('Classification success:', {
        event: event.title,
        result: classification,
        attempt: retryCount + 1
      });

      if (!classification.category || !classification.type) {
        throw new Error(`Invalid classification result for "${event.title}": missing category or type`);
      }

      await sleep(1000);
      setProcessingQueue(false);

      return classification as ClassificationResult;
    } catch (error) {
      const errorDetails = logError(error, `Classification attempt ${retryCount + 1} for "${event.title}"`);
      
      if (retryCount < MAX_RETRIES) {
        setClassificationRetries(prev => prev + 1);
        setProcessingQueue(false);
        // Add longer delay for 500 errors
        if (error.message.includes('500') || error.message.includes('temporarily unavailable')) {
          await sleep(5000); // Wait 5 seconds before retry on 500 errors
        }
        return classifyEventWithRetry(event, retryCount + 1);
      }
      
      console.warn('Using default classification after max retries:', {
        event: event.title,
        error: errorDetails
      });
      setProcessingQueue(false);
      
      // Try to infer a better category based on the event title
      const inferredCategory = inferEventCategory(event.title);
      
      return {
        category: inferredCategory.category,
        type: inferredCategory.type
      };
    }
  };

  // Helper function to infer event category from title
  const inferEventCategory = (title: string): { category: EventCategory; type: string } => {
    const titleLower = title.toLowerCase();
    
    if (titleLower.includes('concert') || titleLower.includes('music') || titleLower.includes('live')) {
      return {
        category: 'Live Show & Music',
        type: 'Concerts & Gigs'
      };
    }
    if (titleLower.includes('party') || titleLower.includes('club')) {
      return {
        category: 'Party',
        type: 'Clubbing'
      };
    }
    if (titleLower.includes('art') || titleLower.includes('exhibition') || titleLower.includes('gallery')) {
      return {
        category: 'Art & Exhibitions',
        type: 'Exhibitions & Galleries'
      };
    }
    if (titleLower.includes('food') || titleLower.includes('dinner') || titleLower.includes('restaurant')) {
      return {
        category: 'Food & Drink',
        type: 'Special Nights & Menus'
      };
    }
    if (titleLower.includes('market') || titleLower.includes('fair')) {
      return {
        category: 'Markets & Pop-Ups',
        type: 'Pop-up Market'
      };
    }
    
    // Default fallback
    return {
      category: 'Social Meetups',
      type: 'Casual Hangouts'
    };
  };

  React.useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      if (!processing) return;

      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();

        if (e.key.toLowerCase() === 's') {
          handleSkip();
        } else if (e.key.toLowerCase() === 'a') {
          try {
            const currentEvent = events[currentIndex];
            if (currentEvent) {
              setAiClassifying(true);
              
              const classification = await classifyEventWithRetry(currentEvent);
              
              const updatedEvent: Event = {
                ...currentEvent,
                category: classification.category,
                type: classification.type,
                event_type: classification.category
              };

              const newEvents = [...events];
              newEvents[currentIndex] = updatedEvent;
              setEvents(newEvents);

              if (classification.category === 'Social Meetups') {
                setFailedEvents(prev => prev + 1);
              }

              await handleNext(updatedEvent);
            }
          } catch (error) {
            console.error('Error in AI classification and upload:', error);
            setFailedEvents(prev => prev + 1);
          } finally {
            setAiClassifying(false);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [processing, currentIndex, events]);

  useEffect(() => {
    if (events.length > 0 && currentIndex < events.length) {
      const currentEvent = events[currentIndex];
      
      if (currentEvent.special_event === 'movie') {
        handleNext(currentEvent);
      }
    }
  }, [currentIndex, events]);

  const processTrailer = async (trailerUrl: string): Promise<string | null> => {
    try {
      setTrailerStatus('Starting trailer processing...');
      const response = await fetch('https://us-central1-YOUR_PROJECT_ID.cloudfunctions.net/processTrailer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ trailerUrl })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.url) {
        setTrailerStatus('Trailer processed successfully');
        return data.url;
      }
      
      setTrailerStatus('Failed to process video');
      return null;
    } catch (error) {
      setTrailerStatus('Error processing trailer');
      console.error('Error in processTrailer:', error);
      return null;
    }
  };

  const compressTrailer = async (videoBlob: Blob): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      setTrailerStatus('Setting up video processing...');
      const video = document.createElement('video');
      video.src = URL.createObjectURL(videoBlob);
      
      video.onloadedmetadata = () => {
        setTrailerStatus(`Video loaded, duration: ${video.duration}s`);
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        const duration = Math.min(30, video.duration);
        
        setTrailerStatus('Starting compression...');
        const stream = canvas.captureStream();
        const recorder = new MediaRecorder(stream, {
          mimeType: 'video/webm',
          videoBitsPerSecond: 1000000
        });
        
        const chunks: Blob[] = [];
        recorder.ondataavailable = e => chunks.push(e.data);
        
        recorder.onstop = () => {
          setTrailerStatus('Finalizing compressed video...');
          const compressedBlob = new Blob(chunks, { type: 'video/mp4' });
          resolve(compressedBlob);
        };
        
        const drawFrame = () => {
          if (video.currentTime < duration) {
            ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
            requestAnimationFrame(drawFrame);
          } else {
            recorder.stop();
            video.pause();
          }
        };

        recorder.start();
        video.currentTime = 0;
        video.play();
        drawFrame();
      };
      
      video.onerror = (e) => {
        setTrailerStatus(`Error processing video: ${e}`);
        reject(e);
      };
    });
  };

  const getHostCoordinates = async (hostId: string): Promise<GeoPoint> => {
    try {
      const hostDoc = await getDoc(doc(db, 'Hosts', hostId));
      if (hostDoc.exists()) {
        const hostData = hostDoc.data();
        if (hostData.latLong instanceof GeoPoint) {
          return hostData.latLong;
        }
      }
      return new GeoPoint(32.04081667021916, 34.76545583456755);
    } catch (error) {
      console.error('Error fetching host coordinates:', error);
      return new GeoPoint(32.04081667021916, 34.76545583456755);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const jsonData = JSON.parse(event.target?.result as string);
        const parsedEvents = await parseJsonEvents(Array.isArray(jsonData) ? jsonData : [jsonData]);
        
        setEvents(parsedEvents);
        setProcessing(true);
        setCurrentIndex(0);
        
        if (parsedEvents.length > 0) {
          setMissingFields(validateEvent(parsedEvents[0]));
        }
      } catch (error) {
        console.error('Error parsing JSON:', error);
      }
    };
    reader.readAsText(file);
  };

  const validateEvent = (event: Event): MissingFields => {
    const missing: MissingFields = {};
    
    if (!event.title) missing.title = true;
    if (!event.event_type) missing.event_type = true;
    if (!event.lat_long || 
        typeof event.lat_long.latitude !== 'number' || 
        typeof event.lat_long.longitude !== 'number') {
      missing.lat_long = true;
    }

    return missing;
  };

  const areCoordinatesNearby = (coord1: GeoPoint, coord2: GeoPoint): boolean => {
    const R = 6371000; // Earth's radius in meters
    const lat1 = coord1.latitude * Math.PI / 180;
    const lat2 = coord2.latitude * Math.PI / 180;
    const dLat = (coord2.latitude - coord1.latitude) * Math.PI / 180;
    const dLon = (coord2.longitude - coord1.longitude) * Math.PI / 180;

    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1) * Math.cos(lat2) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;

    return distance <= 20; // 20 meters
  };

  const findNearbyEvents = async (event: Event): Promise<Event[]> => {
    try {
      const allEvents = await eventService.getAllEvents();
      
      return allEvents.filter(existingEvent => 
        existingEvent.title.toLowerCase() === event.title.toLowerCase()
      );

    } catch (error) {
      console.error('Error in findNearbyEvents:', error);
      return [];
    }
  };

  const parseJsonEvents = async (jsonEvents: (FacebookJsonEvent | TelAvivJsonEvent | CinemaJsonEvent | MevalimJsonEvent)[]): Promise<Event[]> => {
    setIsUploading(true);
    setUploadProgress('Processing events...');
    
    const processedEvents = await Promise.all(jsonEvents.map(async (jsonEvent, index) => {
      setUploadProgress(`Processing event ${index + 1} of ${jsonEvents.length}`);
      
      let event: Event;
      try {
        if (isCinemaEvent(jsonEvent)) {
          event = await eventParsers.parseCinemaEvent(jsonEvent as CinemaJsonEvent);
        } else if (isFunzingEvent(jsonEvent)) {
          event = await eventParsers.parseFunzingEvent(jsonEvent as FunzingJsonEvent);
        } else if (isTelAvivEvent(jsonEvent)) {
          const parsedEvent = await eventParsers.parseTelAvivEvent(jsonEvent as TelAvivJsonEvent);
          if (parsedEvent) {
            event = parsedEvent;
          } else {
            console.log('Skipping Tel Aviv event due to empty location or duplicate');
            return null;
          }
        } else if (isFacebookEvent(jsonEvent)) {
          event = await eventParsers.parseFacebookEvent(jsonEvent as FacebookJsonEvent);
        } else if (isMevalimEvent(jsonEvent)) {
          const parsedEvent = await eventParsers.parseMevalimEvent(jsonEvent as MevalimJsonEvent);
          if (parsedEvent) {
            event = parsedEvent;
          } else {
            console.log('Skipping Mevalim event due to empty location or duplicate');
            return null;
          }
        } else {
          throw new Error('Unknown event type');
        }

        if (event.special_event === 'movie') {
          return event;
        }

        const duplicates = await findNearbyEvents(event);
        const hasDuplicates = duplicates.length > 0;

        const missing = validateEvent(event);
        const hasCriticalMissing = ['title', 'event_type', 'lat_long'].some(field => missing[field]);

        if (!hasCriticalMissing && !hasDuplicates && automaticMode) {
          try {
            const eventToCreate = { 
              ...event,
              Image_gallery_url: [],
              poster_url: '',
              imageUrl: ''
            };
            
            delete eventToCreate._localImageFiles;
            delete eventToCreate._reviewReason;
            delete eventToCreate._nearbyEvents;
            delete eventToCreate._missingFields;

            const eventId = await eventService.createEvent(eventToCreate, []);
            console.log('Created event with ID:', eventId);

            if (event._localImageFiles?.length) {
              try {
                const uploadedUrls = await imageService.processImages(event._localImageFiles, eventId);
                console.log('Successfully uploaded images:', uploadedUrls);

                await eventService.updateEvent(eventId, {
                  Image_gallery_url: uploadedUrls,
                  poster_url: uploadedUrls[0] || '',
                  imageUrl: uploadedUrls[0] || ''
                });
                console.log('Updated event with image URLs');
              } catch (error) {
                console.error('Error uploading images:', error);
              }
            }

            return null;
          } catch (error) {
            console.error('Error auto-uploading event:', error);
            return {
              ...event,
              _missingFields: missing,
              _nearbyEvents: duplicates,
              _reviewReason: {
                missingFields: hasCriticalMissing ? Object.keys(missing) : [],
                hasConflict: hasDuplicates,
                conflictingEvents: duplicates.map(e => ({
                  title: e.title,
                  place: e.place,
                  time: e.startDateTime instanceof Timestamp ? e.startDateTime :
                        Array.isArray(e.startDateTime) ? e.startDateTime :
                        Timestamp.fromDate(new Date(e.startDateTime as string))
                }))
              }
            };
          }
        }

        return {
          ...event,
          _missingFields: missing,
          _nearbyEvents: duplicates,
          _reviewReason: {
            missingFields: hasCriticalMissing ? Object.keys(missing) : [],
            hasConflict: hasDuplicates,
            conflictingEvents: duplicates.map(e => ({
              title: e.title,
              place: e.place,
              time: e.startDateTime instanceof Timestamp ? e.startDateTime :
                    Array.isArray(e.startDateTime) ? e.startDateTime :
                    Timestamp.fromDate(new Date(e.startDateTime as string))
            }))
          }
        };
      } catch (error) {
        console.error('Error processing event:', error);
        return null;
      }
    }));

    setIsUploading(false);
    console.log('Events to process:', processedEvents);
    return processedEvents.filter((event): event is Event => event !== null);
  };

  const isFunzingEvent = (event: any): event is FunzingJsonEvent => {
    return event.source === 'funzing';
  };
  const isFacebookEvent = (event: any): event is FacebookJsonEvent => {
    return ('location' in event || 'imageUrl' in event) && event.source !== 'mevalim';
  };

  const isTelAvivEvent = (event: any): event is TelAvivJsonEvent => {
    return event.source === 'gov';
  };

  const isCinemaEvent = (event: any): event is CinemaJsonEvent => {
    return event.special_event === 'movie' && 'duration' in event;
  };

  const isMevalimEvent = (event: any): event is MevalimJsonEvent => {
    return event.source === 'mevalim';
  };

  const parseDateTime = (date: string, time: string): Timestamp => {
    try {
      const cleanTime = time.replace(/[^\d:]/g, '');
      const [hours, minutes] = cleanTime.split(':').map(Number);
      
      const [year, month, day] = date.split('-').map(Number);
      
      if (!year || !month || !day || isNaN(hours) || isNaN(minutes)) {
        console.warn('Invalid date/time components:', { year, month, day, hours, minutes });
        return Timestamp.fromDate(new Date());
      }
      
      const dateObj = new Date(year, month - 1, day, hours, minutes);
      
      if (isNaN(dateObj.getTime())) {
        console.warn('Invalid date created:', dateObj);
        return Timestamp.fromDate(new Date());
      }
      
      return Timestamp.fromDate(dateObj);
    } catch (error) {
      console.error('Error parsing date/time:', error);
      return Timestamp.fromDate(new Date());
    }
  };

  const parseOriginalEvent = async (jsonEvent: TelAvivJsonEvent): Promise<Event> => {
    try {
      const allImages = [
        jsonEvent.imageUrl,
      ]
        .filter((url): url is string => Boolean(url && url.includes('tel-aviv.gov.il')));

      const processedImages = await imageService.fetchAndProcessImages(allImages);

      let startDateTime: Timestamp[];
      let endDateTime: Timestamp[];

      if (Array.isArray(jsonEvent.dates) && jsonEvent.dates.length > 0) {
        const parsedDates = jsonEvent.dates.map(date => {
          try {
            const [datePart, timePart] = date.split(' ');
            if (!datePart || !timePart) {
              throw new Error('Invalid date format');
            }

            const [startTime, endTime] = timePart.split('-');
            if (!startTime || !endTime) {
              throw new Error('Invalid time format');
            }
            
            const [day, month, yearShort] = datePart.split('.').map(Number);
            if (!day || !month || yearShort === undefined) {
              throw new Error('Invalid date components');
            }
            const year = 2000 + yearShort;
            
            const [startHour, startMinute] = startTime.split(':').map(Number);
            const [endHour, endMinute] = endTime.split(':').map(Number);
            if (isNaN(startHour) || isNaN(startMinute) || isNaN(endHour) || isNaN(endMinute)) {
              throw new Error('Invalid time components');
            }
            
            const startDate = new Date(year, month - 1, day, startHour, startMinute);
            const endDate = new Date(year, month - 1, day, endHour, endMinute);
            
            if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
              throw new Error('Invalid date objects created');
            }

            return {
              startTime: Timestamp.fromDate(startDate),
              endTime: Timestamp.fromDate(endDate)
            };
          } catch (error) {
            console.warn('Error parsing date:', error, date);
            throw error;
          }
        });

        startDateTime = parsedDates.map(d => d.startTime);
        endDateTime = parsedDates.map(d => d.endTime);
      } else {
        throw new Error('No valid dates provided');
      }

      let lat_long: GeoPoint;
      if (jsonEvent.location?.coordinates?.latitude && jsonEvent.location?.coordinates?.longitude) {
        lat_long = new GeoPoint(
          jsonEvent.location.coordinates.latitude,
          jsonEvent.location.coordinates.longitude
        );
      } else {
        lat_long = new GeoPoint(32.04081667021916, 34.76545583456755);
      }

      const baseEvent = {
        title: jsonEvent.name,
        name: jsonEvent.name,
        description: jsonEvent.description,
        place: jsonEvent.location?.name || jsonEvent.place,
        event_type: jsonEvent.event_type || 'Other',
        isPublic: true,
        date: startDateTime[0].toDate(),
        lat_long,
        userId: 'K09eqzpzpOYAzcdl8k2vtujaJLO2',
        user_name: 'tel_aviv_import',
        likes: 0,
        comments: 0,
        shares: 0,
        Image_gallery_url: processedImages.map(img => img.url),
        poster_url: processedImages[0]?.url || '',
        imageUrl: processedImages[0]?.url || '',
        event_url: jsonEvent.url || jsonEvent.event_url || '',
        startDateTime,
        endDateTime,
        date_scraped: jsonEvent.date_scraped,
        _localImageFiles: processedImages.map(img => img.file),
        is_recurring: jsonEvent.is_recurring
      };

      return baseEvent;
    } catch (error) {
      console.error('Error parsing event:', error);
      throw error;
    }
  };

  const parseDateString = (dateStr: string): { startTime: Timestamp; endTime: Timestamp | null } => {
    try {
      const [datePart, timePart] = dateStr.split(' ');
      const [day, month, year] = datePart.split('.').map(Number);
      const [startTime, endTime] = timePart.split('-');
      
      const [startHour, startMinute] = startTime.split(':').map(Number);
      const startDateTime = new Date(2000 + year, month - 1, day, startHour, startMinute);
      
      let endDateTime: Date | null = null;
      if (endTime) {
        const [endHour, endMinute] = endTime.split(':').map(Number);
        endDateTime = new Date(2000 + year, month - 1, day, endHour, endMinute);
      }

      return {
        startTime: Timestamp.fromDate(startDateTime),
        endTime: endDateTime ? Timestamp.fromDate(endDateTime) : null
      };
    } catch (error) {
      console.error('Error parsing date string:', error);
      return {
        startTime: Timestamp.now(),
        endTime: null
      };
    }
  };

  const parseEventDateTime = (dateString: string): { startTime: Timestamp; endTime: Timestamp | null } => {
    try {
      if (!dateString) {
        console.warn('No date string provided, using current time');
        return {
          startTime: Timestamp.now(),
          endTime: null
        };
      }

      const [datePart, timePartWithIST] = dateString.split(' at ');
      
      const timePart = timePartWithIST.replace(/\s+IST$/, '').trim();
      
      const hasTimeRange = timePart.includes('–');
      
      if (hasTimeRange) {
        const [startTime, endTime] = timePart.split('–').map(t => t.trim());
        
        const startDateTime = new Date(`${datePart} ${startTime}`);
        
        const [endHour, endMinute] = endTime.match(/(\d+):(\d+)/)?.slice(1).map(Number) || [];
        const endDateTime = new Date(startDateTime);
        endDateTime.setHours(
          endTime.includes('PM') && endHour !== 12 ? endHour + 12 : endHour,
          endMinute
        );

        if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
          throw new Error(`Invalid date format: ${dateString}`);
        }

        return {
          startTime: Timestamp.fromDate(startDateTime),
          endTime: Timestamp.fromDate(endDateTime)
        };
      } else {
        const startDateTime = new Date(`${datePart} ${timePart}`);
        
        if (isNaN(startDateTime.getTime())) {
          throw new Error(`Invalid date format: ${dateString}`);
        }

        const endDateTime = new Date(startDateTime.getTime() + (2 * 60 * 60 * 1000));

        return {
          startTime: Timestamp.fromDate(startDateTime),
          endTime: Timestamp.fromDate(endDateTime)
        };
      }
    } catch (error) {
      console.error('Error parsing event date time:', error, dateString);
      return {
        startTime: Timestamp.now(),
        endTime: null
      };
    }
  };

  const calculateEndTime = (startTime: Timestamp, durationMinutes: number): Timestamp => {
    const endDate = new Date(startTime.seconds * 1000);
    endDate.setMinutes(endDate.getMinutes() + durationMinutes);
    return Timestamp.fromDate(endDate);
  };

  const handleNext = async (event: Event) => {
    try {
      const eventToCreate = { 
        ...event,
        event_type: event.special_event === 'movie' ? 'Art' : event.event_type,
        Image_gallery_url: [],
        poster_url: '',
        imageUrl: ''
      };
      
      delete eventToCreate._localImageFiles;
      delete eventToCreate._reviewReason;
      delete eventToCreate._nearbyEvents;
      delete eventToCreate._missingFields;

      const eventId = await eventService.createEvent(eventToCreate, []);
      console.log('Created event with ID:', eventId);

      if (event._localImageFiles?.length) {
        try {
          const uploadedUrls = await imageService.processImages(event._localImageFiles, eventId);
          console.log('Successfully uploaded images:', uploadedUrls);

          await eventService.updateEvent(eventId, {
            Image_gallery_url: uploadedUrls,
            poster_url: uploadedUrls[0] || '',
            imageUrl: uploadedUrls[0] || ''
          });
          console.log('Updated event with image URLs');
        } catch (error) {
          console.error('Error uploading images:', error);
        }
      }

      if (currentIndex < events.length - 1) {
        setCurrentIndex(prev => prev + 1);
      } else {
        onClose();
      }
    } catch (error) {
      console.error('Error in handleNext:', error);
    }
  };

  const handleSkip = () => {
    if (currentIndex < events.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      onClose();
    }
  };

  const handlePrevious = () => {
    setCurrentIndex(prev => prev - 1);
  };

  const handleAutomaticUpload = async () => {
    setIsUploading(true);
    const withMissingFields: Event[] = [];
    let uploadedCount = 0;

    for (let i = currentIndex; i < events.length; i++) {
      const event = events[i];
      const missing = validateEvent(event);
      
      if (Object.keys(missing).length > 0) {
        console.log(`Event "${event.title || 'Untitled'}" needs review:`, missing);
        withMissingFields.push(event);
        continue;
      }

      const nearbyEvents = await findNearbyEvents(event);
      if (nearbyEvents.length > 0) {
        console.log(`Event "${event.title}" has nearby conflicts:`, 
          nearbyEvents.map(e => e.title));
        event._nearbyEvents = nearbyEvents;
        withMissingFields.push(event);
        continue;
      }

      try {
        setUploadProgress(`Auto-uploading event ${i + 1} of ${events.length}`);
        await handleNext(event);
        uploadedCount++;
      } catch (error) {
        console.error('Error uploading event:', error);
        withMissingFields.push(event);
      }
    }

    setAutoUploadCount(uploadedCount);
    
    if (withMissingFields.length > 0) {
      console.log(`${withMissingFields.length} events need review`);
      setEvents(withMissingFields);
      setCurrentIndex(0);
      setMissingFields(validateEvent(withMissingFields[0]));
      setProcessing(true);
    } else {
      onClose();
    }
    
    setIsUploading(false);
  };

  const UploadOverlay = () => (
    <div className="upload-overlay">
      <div className="upload-modal">
        <div className="spinner"></div>
        <h3>Uploading Events</h3>
        <p>{uploadProgress}</p>
      </div>
    </div>
  );

  const testEvent = {
    "name": "Jazz Night at the Harbor",
    "description": "Join us for an evening of smooth jazz by the waterfront. Live performances from local artists.",
    "place": "Tel Aviv Port",
  };

  const handleJumpToEvent = (e: React.FormEvent) => {
    e.preventDefault();
    const eventNum = parseInt(eventNumberInput) - 1;
    if (eventNum >= 0 && eventNum < events.length) {
      setCurrentIndex(eventNum);
      setEventNumberInput('');
    } else {
      alert(`Please enter a number between 1 and ${events.length}`);
    }
  };

  const progressInfo = (
    <div className="progress-info">
      <div>Event {currentIndex + 1} of {events.length}</div>
      {failedEvents > 0 && (
        <div className="failed-events-info">
          Failed classifications: {failedEvents}
        </div>
      )}
      {classificationRetries > 0 && (
        <div className="retry-info">
          Classification retries: {classificationRetries}
        </div>
      )}
      {lastError && (
        <div className="error-info" style={{ color: 'red', marginTop: '10px' }}>
          Last error: {lastError}
        </div>
      )}
      {processingQueue && (
        <div className="queue-info" style={{ color: 'blue', marginTop: '10px' }}>
          Waiting for previous classification to complete...
        </div>
      )}
    </div>
  );

  return (
    <div className="json-import-container">
      {isUploading && <UploadOverlay />}
      {!processing ? (
        <>
          <h2>Import Events from JSON</h2>
          <div className="import-options">
            <label className="automatic-mode-toggle">
              <input
                type="checkbox"
                checked={automaticMode}
                onChange={async (e) => {
                  setAutomaticMode(e.target.checked);
                  if (e.target.checked && events.length > 0) {
                    await handleAutomaticUpload();
                  }
                }}
              />
              Enable automatic upload for valid events
            </label>
          </div>
          <input
            type="file"
            accept=".json"
            onChange={handleFileUpload}
            className="file-input"
          />
        </>
      ) : (
        <>
          <div className="import-header">
            <h2>Review Required Events</h2>
            {autoUploadCount > 0 && (
              <div className="auto-upload-info">
                Successfully auto-uploaded {autoUploadCount} events
              </div>
            )}
            {progressInfo}
            <form onSubmit={handleJumpToEvent} className="event-jump-form">
              <input
                type="number"
                min="1"
                max={events.length}
                value={eventNumberInput}
                onChange={(e) => setEventNumberInput(e.target.value)}
                placeholder="Jump to #"
                className="event-number-input"
              />
              <button type="submit" className="button secondary">Go</button>
            </form>
          </div>

          {aiClassifying && (
            <div className="ai-classifying-indicator">
              <span className="ai-icon">🤖</span>
              <div>
                <div>AI is analyzing events...</div>
                <div className="ai-subtitle">Determining event types for uncategorized events</div>
              </div>
            </div>
          )}

          <EventForm
            event={events[currentIndex]}
            onSubmit={() => handleNext(events[currentIndex])}
            missingFields={missingFields}
          />

          <div className="import-actions">
            <button 
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className="button secondary"
            >
              Previous
            </button>
            <button 
              onClick={handleSkip} 
              className="button secondary"
            >
              Skip
            </button>
            <button 
              onClick={() => handleNext(events[currentIndex])} 
              className="button primary"
            >
              {currentIndex === events.length - 1 ? 'Finish' : 'Next'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}; 